import { Component, Input } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import * as R from 'ramda';
import { takeUntil } from 'rxjs/operators';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import {
    AUDIT_DIFF_MODAL,
    FormPendingChange,
    TranslateTokenisedStringPipe,
    TRANSLATION_AUDIT_DIFF_COUNT_MULTIPLE,
    TRANSLATION_AUDIT_DIFF_COUNT_SINGLE,
    TRANSLATION_AUDIT_DIFF_NO_DATA_FOUND,
    TRANSLATION_AUDIT_DIFF_POST,
    TRANSLATION_AUDIT_DIFF_PRE,
    TRANSLATION_AUDIT_DIFF_SHOW_ONLY_DIFF,
    TRANSLATION_AUDIT_DIFF_TAB_JSON,
    TRANSLATION_AUDIT_DIFF_TAB_TABLE,
    TRANSLATION_AUDIT_EVENT_DETAILS_TITLE,
    TranslationsService,
} from '@wdx/shared/utils';
import { WdxTabsModule } from '@wdx/shared/components/wdx-tabs';
import { WdxSwitchModule } from '@wdx/shared/components/wdx-switch';
import { WdxJsonDiffComponent } from '@wdx/shared/components/wdx-json-diff';
import { WdxAlertModule } from '@wdx/shared/components/wdx-alert';
import { WdxIconModule } from '@wdx/shared/components/wdx-icon';
import { ConfigService } from '../../../services/config.service';
import { ModalManagementService } from '@wdx/clmi/ui';
import { BaseComponentClass } from '../../../classes/base-component.class';
import { AtomModalWrapperModule } from '../../atoms/atom-modal-wrapper/atom-modal-wrapper.module';
import { OrganismModalModule } from '../organism-modal/organism-modal.module';
import { OrganismAuditDiffCellRendererComponent } from './audit-diff-cell-renderer/organism-audit-diff-cell-renderer.component';

export interface IAuditEventComparisonRow {
    field: string;
    preValue: string;
    postValue: string;
    isDifferent: boolean;
}

interface IAuditEventFormData {
    data?: {
        pre?: string;
        post?: string;
    };
}

@Component({
    selector: 'organism-audit-diff-modal',
    templateUrl: './organism-audit-diff-modal.component.html',
    standalone: true,
    imports: [
        CommonModule,
        AgGridAngular,
        AtomModalWrapperModule,
        OrganismModalModule,
        WdxTabsModule,
        WdxSwitchModule,
        WdxJsonDiffComponent,
        WdxAlertModule,
        WdxIconModule,
        TranslateTokenisedStringPipe,
    ],
})
export class OrganismAuditDiffModalComponent extends BaseComponentClass {
    @Input() modalId: string;

    public auditEventId: string;
    public title = TRANSLATION_AUDIT_EVENT_DETAILS_TITLE;

    public auditEventComparisonRows: IAuditEventComparisonRow[] = [];
    public columnDefs: ColDef<IAuditEventComparisonRow>[] = [
        {
            field: 'preValue',
            headerName: TRANSLATION_AUDIT_DIFF_PRE,
            flex: 1,
            cellRenderer: OrganismAuditDiffCellRendererComponent,
        },
        {
            field: 'postValue',
            headerName: TRANSLATION_AUDIT_DIFF_POST,
            flex: 1,
            cellRenderer: OrganismAuditDiffCellRendererComponent,
        },
    ];
    public auditEventRawData: { pre: unknown; post: unknown } | null = null;
    public auditEventFilteredData: { pre: unknown; post: unknown } | null =
        null;
    public defaultColDef: ColDef = {
        resizable: true,
        sortable: false,
        wrapText: true,
        autoHeight: true,
        headerClass: 'px-3',
        cellClass: 'p-0',
    };

    private _showOnlyDifferences = true;
    public get showOnlyDifferences(): boolean {
        return this._showOnlyDifferences;
    }
    public set showOnlyDifferences(value: boolean) {
        this._showOnlyDifferences = value;
        this.updateFilteredRows();
    }

    public filteredAuditEventComparisonRows: IAuditEventComparisonRow[] = [];
    public differences: FormPendingChange[] = [];

    public readonly AUDIT_DIFFERENCES_MAPPING = {
        '=1': TRANSLATION_AUDIT_DIFF_COUNT_SINGLE,
        other: TRANSLATION_AUDIT_DIFF_COUNT_MULTIPLE,
    };

    public readonly TRANSLATION_AUDIT_DIFF_TAB_TABLE =
        TRANSLATION_AUDIT_DIFF_TAB_TABLE;
    public readonly TRANSLATION_AUDIT_DIFF_TAB_JSON =
        TRANSLATION_AUDIT_DIFF_TAB_JSON;
    public readonly TRANSLATION_AUDIT_DIFF_NO_DATA_FOUND =
        TRANSLATION_AUDIT_DIFF_NO_DATA_FOUND;
    public readonly TRANSLATION_AUDIT_DIFF_SHOW_ONLY_DIFF =
        TRANSLATION_AUDIT_DIFF_SHOW_ONLY_DIFF;

    public showAdvancedMode = false;
    public auditEventNoData = false;
    public isLoading = true;

    constructor(
        private http: HttpClient,
        private config: ConfigService,
        private translationsService: TranslationsService,
        modalManagementService: ModalManagementService,
    ) {
        super();
        this.patchInjectedItems({ modalManagementService });

        this.columnDefs = this.columnDefs.map((col) => ({
            ...col,
            headerName: this.translationsService.translateTokenisedString(
                col.headerName,
            ),
        }));

        this.modalManagementService.latestInstanceParams$
            .pipe(takeUntil(this.destroyed$))
            .subscribe((params) => {
                if (
                    params?.modalId === AUDIT_DIFF_MODAL &&
                    params?.modalParams?.auditEventId
                ) {
                    const newId = params.modalParams.auditEventId;
                    if (this.auditEventId !== newId) {
                        this.auditEventId = newId;
                        this.title =
                            params.modalParams.title ||
                            TRANSLATION_AUDIT_EVENT_DETAILS_TITLE;
                        this.loadAuditEvent();
                    }
                } else {
                    this.modalId = params?.modalId;
                }
            });
    }

    getTitle(): string {
        return this.translationsService.translateTokenisedString(this.title);
    }

    private updateFilteredRows() {
        if (this.showOnlyDifferences) {
            this.filteredAuditEventComparisonRows =
                this.auditEventComparisonRows.filter((row) => row.isDifferent);
        } else {
            this.filteredAuditEventComparisonRows = [
                ...this.auditEventComparisonRows,
            ];
        }
    }

    private loadAuditEvent() {
        this.isLoading = true;
        const apiBase = this.config.getConfiguration().API_BASE;
        this.http
            .get<IAuditEventFormData>(
                `${apiBase}/formdata/AuditEventForm/${this.auditEventId}`,
            )
            .subscribe({
                next: (formData) => {
                    this.calculateAuditEventDifferences(formData);
                    this.isLoading = false;
                },
                error: () => {
                    this.auditEventNoData = true;
                    this.isLoading = false;
                },
            });
    }

    private calculateAuditEventDifferences(formData: IAuditEventFormData) {
        if (!formData?.data?.pre && !formData?.data?.post) {
            this.auditEventNoData = true;
            this.auditEventComparisonRows = [];
            this.differences = [];
            this.updateFilteredRows();
            return;
        }

        this.auditEventNoData = false;

        try {
            const preData: unknown = formData?.data?.pre
                ? JSON.parse(formData.data.pre)
                : {};
            const postData: unknown = formData?.data?.post
                ? JSON.parse(formData.data.post)
                : {};

            this.auditEventRawData = { pre: preData, post: postData };

            const pre = this.flattenObject(preData);
            const post = this.flattenObject(postData);

            const allComparisonRows: IAuditEventComparisonRow[] = [];

            const allKeys = new Set([
                ...Object.keys(pre),
                ...Object.keys(post),
            ]);
            const sortedKeys = Array.from(allKeys).sort();

            sortedKeys.forEach((key) => {
                const valPre = pre[key];
                const valPost = post[key];

                const preDisplay = this.getDisplayValue(valPre);
                const postDisplay = this.getDisplayValue(valPost);
                let isDifferent = !R.equals(valPre, valPost);

                if (isDifferent && preDisplay === postDisplay) {
                    isDifferent = false;
                }

                allComparisonRows.push({
                    field: key,
                    preValue: preDisplay,
                    postValue: postDisplay,
                    isDifferent: isDifferent,
                });
            });

            this.auditEventComparisonRows = allComparisonRows;
            this.differences = allComparisonRows
                .filter((row) => row.isDifferent)
                .map((row) => ({
                    path: row.field,
                    originalValue: row.preValue,
                    targetValue: row.postValue,
                    originalDisplayValue: row.preValue,
                    targetDisplayValue: row.postValue,
                    name: row.field,
                }));
            this.updateFilteredRows();

            const changedPaths = allComparisonRows
                .filter((row) => row.isDifferent)
                .map((row) => row.field);

            this.auditEventFilteredData = {
                pre: this.filterObjectByPaths(preData, changedPaths),
                post: this.filterObjectByPaths(postData, changedPaths),
            };
        } catch (e) {
            this.auditEventNoData = true;
            this.differences = [];
        }
    }

    private filterObjectByPaths(obj: unknown, paths: string[]): unknown {
        if (!obj || !paths.length) {
            return {};
        }

        const result: any = Array.isArray(obj) ? [] : {};

        paths.forEach((path) => {
            // Convert path[0].prop to path.0.prop for easier splitting
            const normalizedPath = path
                .replace(/\[(\d+)\]/g, '.$1')
                .replace(/^\./, '');
            const parts = normalizedPath.split('.');
            let currentSrc: any = obj;
            let currentDest: any = result;

            for (let i = 0; i < parts.length; i++) {
                const part = parts[i];
                if (
                    currentSrc &&
                    Object.prototype.hasOwnProperty.call(currentSrc, part)
                ) {
                    if (i === parts.length - 1) {
                        currentDest[part] = currentSrc[part];
                    } else {
                        if (!currentDest[part]) {
                            currentDest[part] = Array.isArray(currentSrc[part])
                                ? []
                                : {};
                        }
                        currentSrc = currentSrc[part];
                        currentDest = currentDest[part];
                    }
                } else {
                    break;
                }
            }
        });

        return result;
    }

    private flattenObject(obj: unknown): Record<string, unknown> {
        const result: Record<string, unknown> = {};

        const recurse = (current: unknown, path: string) => {
            if (
                current !== null &&
                typeof current === 'object' &&
                !(current instanceof Date)
            ) {
                const isArray = Array.isArray(current);
                const typedCurrent = current as Record<string, unknown>;
                const keys = Object.keys(typedCurrent);
                if (keys.length === 0) {
                    result[path] = current;
                } else {
                    keys.forEach((key) => {
                        const segment = isArray ? `[${key}]` : `.${key}`;
                        recurse(typedCurrent[key], path ? path + segment : key);
                    });
                }
            } else {
                result[path] = current;
            }
        };

        if (obj !== null && typeof obj === 'object') {
            const typedObj = obj as Record<string, unknown>;
            if (Array.isArray(obj)) {
                obj.forEach((val, index) => recurse(val, `[${index}]`));
            } else {
                Object.keys(typedObj).forEach((key) =>
                    recurse(typedObj[key], key),
                );
            }
        }

        return result;
    }

    private getDisplayValue(value: unknown): string {
        if (value === null || value === undefined || value === '') {
            return '-';
        }
        if (
            typeof value === 'object' &&
            value !== null &&
            !(value instanceof Date)
        ) {
            const v = value as any;
            if (Object.keys(v).length === 0) {
                return '-';
            }
            return v.Label || v.Name || v.name || v.label || JSON.stringify(v);
        }
        return String(value);
    }

    close() {
        this.modalManagementService.closeModalWithId(this.modalId);
    }
}
